import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http } from "./api";
import { PERSONA_TRANSLATIONS } from "./personas";

/**
 * Lightweight i18n. Two languages: ms (default), en.
 * Persistence: localStorage + user profile (backend) when logged in.
 */
const dict = {
  ms: {
    "app.tagline": "Plan Smarter. Travel Better.",
    // Time-based greetings
    "greet.morning": "Selamat pagi.",
    "greet.noon": "Selamat tengah hari.",
    "greet.evening": "Selamat petang.",
    "greet.night": "Selamat malam.",
    "greet.welcome_guest": "Selamat datang.",
    "greet.welcome_back": "Selamat kembali.",
    "landing.heading": "Ke manakah destinasi impian anda?",
    "landing.heading_main": "Ke manakah destinasi impian",
    "landing.heading_script": "anda?",
    "landing.subtitle": "Mari rancang destinasi anda bersama-sama.",
    "landing.prompt": "Pilih persona di atas atau taip destinasi impian anda.",
    "planning.takeoff": "Berlepas",
    "planning.arrival": "Tiba",
    "planning.creating": "Menyusun perancangan anda...",
    "planning.ready": "Perancangan anda sudah siap.",
    "planning.creating_hint": "TAi-P sedang menyediakan perancangan anda dengan teliti.",
    "planning.ready_hint": "Membawa anda ke hasil perancangan sekarang.",
    "planning.back": "Kembali ke homepage",
    "planning.error_title": "Perancangan tergendala seketika.",
    "planning.error_body": "Kami tidak dapat melengkapkannya sekarang. Cuba sekali lagi atau kembali ke homepage.",
    "planning.timeout_title": "Ini mengambil masa lebih lama daripada biasa.",
    "planning.timeout_body": "Anda boleh cuba sekali lagi atau kembali ke homepage tanpa menunggu lagi.",
    "input.placeholder": "Beritahu destinasi impian anda...",
    "input.disclaimer": "Cadangan berdasarkan AI. Sila semak semula harga, ketersediaan, waktu operasi & status halal.",
    "topbar.muslim_friendly": "Muslim Friendly",
    "topbar.muslim_friendly_badge": "Muslim Friendly · Premium",
    "topbar.login": "Log Masuk",
    "topbar.language": "Bahasa",
    "menu.history": "Sejarah Perjalanan",
    "menu.settings": "Tetapan",
    "menu.logout": "Log Keluar",
    "toast.logout_ok": "Log keluar berjaya",
    "toast.chat_error": "Ralat menghubungi TAi-P. Sila cuba lagi.",
    "toast.reading_doc": "Membaca dokumen…",
    "toast.doc_ok": "Dokumen diproses",
    "toast.doc_fail": "Gagal proses dokumen",
    "toast.voice_processing": "Menukar suara kepada teks…",
    "toast.voice_ok": "Suara ditukar",
    "toast.voice_fail": "Gagal transkripsi suara",
    "toast.mic_deny": "Tidak dapat akses mikrofon",
    "toast.login_first": "Sila log masuk untuk menyimpan perjalanan",
    "toast.trip_saved": "Travel Book disimpan",
    "toast.save_fail": "Gagal menyimpan",
    "toast.reset_sent": "Kami hantar pautan set semula ke e-mel anda jika akaun wujud.",
    "toast.reset_ok": "Kata laluan berjaya ditetapkan semula. Sila log masuk.",
    "thinking.hotels": "Sedang mencari hotel terbaik…",
    "thinking.route": "Sedang menyusun laluan…",
    "thinking.food": "Sedang mencari restoran…",
    "thinking.budget": "Sedang mengira bajet…",
    "thinking.itinerary": "Sedang menghasilkan Travel Dossier…",
    // Travel Dossier
    "book.title": "Travel Dossier",
    "book.download": "Muat Turun Travel Dossier (PDF)",
    "book.save": "Simpan Travel Dossier",
    // ItineraryView sections
    "iv.daily_itinerary": "Jadual Harian",
    "iv.hotels": "Cadangan Hotel",
    "iv.map": "Peta",
    "iv.restaurants": "Cadangan Restoran",
    "iv.budget": "Bajet",
    "iv.checklist": "Senarai Semak",
    "iv.tips": "Tips",
    "iv.notes": "Nota",
    "iv.transportation": "Pengangkutan",
    "iv.demo_badge": "Cadangan demo",
    "iv.days": "hari",
    "iv.share": "Kongsi",
    "iv.disclaimer_footer": "Semua cadangan adalah anggaran. Sila sahkan harga, ketersediaan, waktu operasi & status halal sebelum tempahan.",
    "iv.translation_failed": "Terjemahan gagal. Sila cuba semula.",
    "iv.estimated_budget": "Anggaran Bajet",
    // Budget labels
    "budget.hotel": "Hotel",
    "budget.food": "Makanan",
    "budget.transport": "Pengangkutan",
    "budget.shopping": "Shopping",
    "budget.tickets": "Tiket",
    "budget.misc": "Lain-lain",
    "budget.total": "Jumlah",
    // Trip History
    "history.title": "Sejarah Perjalanan",
    "history.login_required": "Log masuk untuk melihat perjalanan tersimpan.",
    "history.empty_title": "Belum ada perjalanan lagi.",
    "history.empty_hint": "Pilih salah satu Travel Persona atau beritahu destinasi impian anda untuk bermula.",
    "history.load_fail": "Kami tidak dapat memuatkan sejarah anda buat masa ini. Sila cuba lagi sebentar.",
    "history.deleted": "Travel Book dipadam",
    "history.delete_fail": "Gagal padam. Sila cuba lagi.",
    // PDF export
    "pdf.magazine": "TAi-P Travel Dossier",
    "pdf.destination": "Destinasi",
    "pdf.days_label": "Hari",
    "pdf.itinerary": "Itinerari",
    "pdf.hotels_restaurants": "Hotel & Restoran",
    "pdf.hotel": "Hotel",
    "pdf.restaurant": "Restoran",
    "pdf.checklist": "Senarai Semak",
    "pdf.budget": "Bajet",
    "pdf.tips": "Tips",
    "pdf.per_night": "mlm",
    "pdf.no_trip": "Tiada data perjalanan. Sila jana semula dari TAi-P.",
    "pdf.footer_note": "Nota: Semua cadangan adalah anggaran AI. Harga, ketersediaan, waktu operasi & status halal perlu disemak semula. — TAi-P (Travel Intelligence Companion)",
    "pdf.popup_blocked": "Sila benarkan pop-up untuk menjana PDF.",
    "pdf.total": "Jumlah",
    "pdf.halal_confirmed": "Halal ✓",
    "pdf.halal_verify": "Halal perlu semak",
    "pdf.halal_unknown": "Halal ?",
    // Friendly errors
    "err.connection": "Maaf, kami menghadapi sedikit masalah untuk mendapatkan maklumat. Sila cuba semula sebentar lagi.",
    "err.retry": "Cuba Lagi",
    "err.chat_failed": "Kami tidak dapat menghubungi TAi-P. Sila cuba lagi sebentar.",
    "err.rate_limited": "Anda telah mencapai had penggunaan buat masa ini. Sila cuba lagi sebentar.",
    "err.session_expired": "Sesi tamat. Sila log masuk semula.",
    // Personas
    ...PERSONA_TRANSLATIONS.ms,
    // Login
    "settings.title": "Tetapan",
    "settings.not_logged_in": "Belum log masuk",
    "settings.not_logged_in_desc": "Log masuk untuk menyimpan, eksport & sejarah",
    "settings.privacy_account": "Privasi & Akaun",
    "settings.privacy": "Dasar Privasi",
    "settings.terms": "Terma Penggunaan",
    "settings.disclaimer": "Penafian",
    "settings.support": "Sokongan",
    "settings.delete_account": "Padam Akaun",
    "settings.language": "Bahasa",
    "settings.confirm_delete_title": "Padam akaun anda?",
    "settings.confirm_delete_desc": "Tindakan ini kekal. Semua Travel Book yang disimpan, sesi & pembelian akan dipadamkan. Anda tidak boleh membalikkan tindakan ini.",
    "common.cancel": "Batal",
    "common.confirm_delete": "Ya, padam",
    "common.deleting": "Memadam…",
    "login.title": "Log Masuk",
    "login.register_title": "Daftar Akaun",
    "login.apple": "Sign in with Apple",
    "login.google": "Log Masuk dengan Google",
    "login.or": "atau",
    "login.name": "Nama",
    "login.name_ph": "Nama anda",
    "login.email": "E-mel",
    "login.password": "Kata Laluan",
    "login.password_ph": "Sekurang-kurangnya 8 aksara",
    "login.remember_me": "Ingat saya",
    "login.forgot": "Lupa kata laluan?",
    "login.submit_login": "Log Masuk",
    "login.submit_register": "Daftar",
    "login.no_account": "Belum ada akaun?",
    "login.has_account": "Sudah ada akaun?",
    "login.register": "Daftar",
    "login.consent": "Dengan meneruskan anda bersetuju dengan",
    "forgot.title": "Set Semula Kata Laluan",
    "forgot.desc": "Masukkan e-mel anda. Kami akan hantar pautan untuk set semula kata laluan.",
    "forgot.submit": "Hantar Pautan",
    "forgot.back_login": "Kembali ke Log Masuk",
    "reset.title": "Kata Laluan Baru",
    "reset.desc": "Masukkan kata laluan baru untuk akaun anda.",
    "reset.submit": "Set Semula Kata Laluan",
    "reset.expired": "Pautan telah tamat tempoh. Sila mohon pautan baru.",
    "paywall.title": "TAi-P Full Access",
    "paywall.premium": "Premium",
    "paywall.headline": "Bayaran satu kali. Tiada langganan. Semua ciri selamanya.",
    "paywall.get": "Anda akan dapat",
    "paywall.buy": "Beli dengan Apple Pay",
    "paywall.buy_ios_only": "Beli (Hanya iOS)",
    "paywall.restore": "Pulihkan Pembelian",
    "paywall.disclaimer": "Pembayaran akan dicaj kepada Apple ID anda. Ini adalah pembelian satu kali; tiada pembaharuan automatik.",
  },
  en: {
    "app.tagline": "Plan Smarter. Travel Better.",
    "greet.morning": "Good morning.",
    "greet.noon": "Good afternoon.",
    "greet.evening": "Good evening.",
    "greet.night": "Good night.",
    "greet.welcome_guest": "Welcome.",
    "greet.welcome_back": "Welcome back.",
    "landing.heading": "What are we planning today?",
    "landing.heading_main": "What are we planning",
    "landing.heading_script": "today?",
    "landing.subtitle": "Let's plan your journey together.",
    "landing.prompt": "Pick a persona above, or type your dream destination.",
    "planning.takeoff": "Take off",
    "planning.arrival": "Arrival",
    "planning.creating": "Creating your journey...",
    "planning.ready": "Your journey is ready.",
    "planning.creating_hint": "TAi-P is shaping your plan with care.",
    "planning.ready_hint": "Taking you to your completed plan now.",
    "planning.back": "Back to homepage",
    "planning.error_title": "Planning paused for a moment.",
    "planning.error_body": "We couldn't complete it right now. Try again or return to the homepage.",
    "planning.timeout_title": "This is taking longer than expected.",
    "planning.timeout_body": "You can try again or return to the homepage without waiting any longer.",
    "input.placeholder": "Tell me your dream destination...",
    "input.disclaimer": "AI suggestions. Please double-check prices, availability, hours & halal status.",
    "topbar.muslim_friendly": "Muslim Friendly",
    "topbar.muslim_friendly_badge": "Muslim Friendly · Premium",
    "topbar.login": "Sign In",
    "topbar.language": "Language",
    "menu.history": "Trip History",
    "menu.settings": "Settings",
    "menu.logout": "Sign Out",
    "toast.logout_ok": "Signed out",
    "toast.chat_error": "Couldn't reach TAi-P. Please try again.",
    "toast.reading_doc": "Reading document…",
    "toast.doc_ok": "Document processed",
    "toast.doc_fail": "Failed to process document",
    "toast.voice_processing": "Converting voice to text…",
    "toast.voice_ok": "Voice converted",
    "toast.voice_fail": "Voice transcription failed",
    "toast.mic_deny": "Can't access microphone",
    "toast.login_first": "Please sign in to save your Travel Book",
    "toast.trip_saved": "Travel Book saved",
    "toast.save_fail": "Failed to save",
    "toast.reset_sent": "If an account exists, we've sent a reset link to your email.",
    "toast.reset_ok": "Password reset successfully. Please sign in.",
    "thinking.hotels": "Finding the best hotels…",
    "thinking.route": "Planning the route…",
    "thinking.food": "Looking for restaurants…",
    "thinking.budget": "Calculating budget…",
    "thinking.itinerary": "Building your Travel Dossier…",
    "book.title": "Travel Dossier",
    "book.download": "Download Travel Dossier (PDF)",
    "book.save": "Save Travel Dossier",
    // ItineraryView sections
    "iv.daily_itinerary": "Daily Itinerary",
    "iv.hotels": "Hotel Recommendations",
    "iv.map": "Map",
    "iv.restaurants": "Restaurant Recommendations",
    "iv.budget": "Budget",
    "iv.checklist": "Checklist",
    "iv.tips": "Tips",
    "iv.notes": "Notes",
    "iv.transportation": "Transportation",
    "iv.demo_badge": "Demo suggestion",
    "iv.days": "days",
    "iv.share": "Share",
    "iv.disclaimer_footer": "All suggestions are estimates. Please verify prices, availability, hours & halal status before booking.",
    "iv.translation_failed": "Translation failed. Please try again.",
    "iv.estimated_budget": "Estimated Budget",
    // Budget labels
    "budget.hotel": "Hotel",
    "budget.food": "Food",
    "budget.transport": "Transportation",
    "budget.shopping": "Shopping",
    "budget.tickets": "Tickets",
    "budget.misc": "Other",
    "budget.total": "Total",
    // Trip History
    "history.title": "Trip History",
    "history.login_required": "Sign in to see your saved trips.",
    "history.empty_title": "No trips yet.",
    "history.empty_hint": "Pick a Travel Persona or tell us your dream destination to start.",
    "history.load_fail": "We couldn't load your history right now. Please try again shortly.",
    "history.deleted": "Travel Book deleted",
    "history.delete_fail": "Couldn't delete. Please try again.",
    // PDF export
    "pdf.magazine": "TAi-P Travel Dossier",
    "pdf.destination": "Destination",
    "pdf.days_label": "Day",
    "pdf.itinerary": "Itinerary",
    "pdf.hotels_restaurants": "Hotels & Restaurants",
    "pdf.hotel": "Hotel",
    "pdf.restaurant": "Restaurant",
    "pdf.checklist": "Checklist",
    "pdf.budget": "Budget",
    "pdf.tips": "Tips",
    "pdf.per_night": "/night",
    "pdf.no_trip": "No trip data. Please generate again from TAi-P.",
    "pdf.footer_note": "Note: All suggestions are AI estimates. Prices, availability, hours & halal status must be verified. — TAi-P (Travel Intelligence Companion)",
    "pdf.popup_blocked": "Please allow pop-ups to generate the PDF.",
    "pdf.total": "Total",
    "pdf.halal_confirmed": "Halal ✓",
    "pdf.halal_verify": "Halal to verify",
    "pdf.halal_unknown": "Halal ?",
    // Friendly errors
    "err.connection": "Sorry, we're having a small hiccup fetching that. Please try again in a moment.",
    "err.retry": "Try Again",
    "err.chat_failed": "We couldn't reach TAi-P. Please try again shortly.",
    "err.rate_limited": "You've reached your usage limit for now. Please try again later.",
    "err.session_expired": "Your session expired. Please sign in again.",
    ...PERSONA_TRANSLATIONS.en,
    "settings.title": "Settings",
    "settings.not_logged_in": "Not signed in",
    "settings.not_logged_in_desc": "Sign in to save, export & view history",
    "settings.privacy_account": "Privacy & Account",
    "settings.privacy": "Privacy Policy",
    "settings.terms": "Terms of Use",
    "settings.disclaimer": "Disclaimer",
    "settings.support": "Support",
    "settings.delete_account": "Delete Account",
    "settings.language": "Language",
    "settings.confirm_delete_title": "Delete your account?",
    "settings.confirm_delete_desc": "This is permanent. All saved Travel Books, sessions & purchases will be deleted. You cannot undo this action.",
    "common.cancel": "Cancel",
    "common.confirm_delete": "Yes, delete",
    "common.deleting": "Deleting…",
    "login.title": "Sign In",
    "login.register_title": "Create Account",
    "login.apple": "Sign in with Apple",
    "login.google": "Sign in with Google",
    "login.or": "or",
    "login.name": "Name",
    "login.name_ph": "Your name",
    "login.email": "Email",
    "login.password": "Password",
    "login.password_ph": "At least 8 characters",
    "login.remember_me": "Remember me",
    "login.forgot": "Forgot password?",
    "login.submit_login": "Sign In",
    "login.submit_register": "Create",
    "login.no_account": "No account yet?",
    "login.has_account": "Already have an account?",
    "login.register": "Create",
    "login.consent": "By continuing you agree to our",
    "forgot.title": "Reset Password",
    "forgot.desc": "Enter your email. We'll send a link to reset your password.",
    "forgot.submit": "Send Link",
    "forgot.back_login": "Back to Sign In",
    "reset.title": "New Password",
    "reset.desc": "Enter a new password for your account.",
    "reset.submit": "Reset Password",
    "reset.expired": "This link has expired. Please request a new one.",
    "paywall.title": "TAi-P Full Access",
    "paywall.premium": "Premium",
    "paywall.headline": "One-time payment. No subscriptions. All features, forever.",
    "paywall.get": "You'll unlock",
    "paywall.buy": "Buy with Apple Pay",
    "paywall.buy_ios_only": "Buy (iOS only)",
    "paywall.restore": "Restore Purchases",
    "paywall.disclaimer": "Payment will be charged to your Apple ID. This is a one-time purchase; no auto-renewal.",
  },
};

const I18nCtx = createContext(null);

function detectDefault() {
  try {
    const saved = localStorage.getItem("taip_lang");
    if (saved === "ms" || saved === "en") return saved;
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("en")) return "en";
  } catch {}
  return "ms";
}

/**
 * Time-of-day greeting key based on local hour.
 * 05:00–11:59 morning · 12:00–14:59 noon · 15:00–18:59 evening · else night
 */
export function greetingKey(now = new Date()) {
  const h = now.getHours();
  if (h >= 5 && h < 12) return "greet.morning";
  if (h >= 12 && h < 15) return "greet.noon";
  if (h >= 15 && h < 19) return "greet.evening";
  return "greet.night";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectDefault());

  const setLang = useCallback((next) => {
    if (next !== "ms" && next !== "en") return;
    setLangState(next);
    // Best-effort: persist to user profile if logged in. Silent-fail if not.
    try {
      http.patch("/users/me", { preferred_lang: next }).catch(() => {});
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem("taip_lang", lang); } catch {}
    try { document.documentElement.setAttribute("lang", lang); } catch {}
  }, [lang]);

  const t = useCallback((key) => {
    return (dict[lang] && dict[lang][key]) || dict.ms[key] || key;
  }, [lang]);

  return (
    <I18nCtx.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nCtx.Provider>
  );
}

export function useT() {
  return useContext(I18nCtx);
}


